import React from "react";
import { pure, withState } from "recompose";
import Slider from "react-slick";
import { PropertyCard, EntityGrid } from "@witbooking/react-components";
import styled from "styled-components";
import classnames from "classnames";
import { ArrowDownIcon, NavigateBeforeIcon, NavigateNextIcon } from "../../theme/assets";
import ResultBlock from "../ResultBlock/ResultBlock";
import Amount from "../Amount/Amount";
import Thumbnail from "../Thumbnail/Thumbnail";
import BookableErrorMessage from "../BookableErrorMessage/BookableErrorMessage";
import { buildImgUrl } from "../../utils/misc";
import constants from "../../utils/constants";
import { ArrowContainer } from "../../utils/ArrowContainer";
import { sizes } from "../../utils/media";

const Container = styled.div`
  > * {
    margin-bottom: 20px;
  }

  > *:first-child {
    margin-bottom: ${props => (props.hasButtonBar ? "20px" : "0")};
  }

  margin: ${props => (props.hasButtonBar ? "10px 0" : "0")};

  .slick-slide :first-child {
    height: 100%;
  }
`;

const StyledAmount = styled(Amount)`
  font-weight: bold;
  font-size: 24px;
  line-height: 22px;
  color: #fff;
  margin: 2px 0;
  padding: 0 9px;
`;

const ThumbnailContainer = styled.div`
  height: 100%;
  width: 135px;
  min-width: 135px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img {
    transition: opacity 0.6s;
    width: 100% !important;
    max-width: 100%;
    min-height: 100%;
    opacity: 1;
  }
`;

const MobileImage = styled.img`
  border-radius: 100%;
  width: 50px;
  height: 50px;
  object-fit: cover;
  margin: 10px 20px 10px 10px;
`;

const StyledPropertyCard = styled(PropertyCard)`
  margin-bottom: 20px;
`;

const sliderSettings = {
  arrows: true,
  autoplay: false,
  centerMode: false,
  infinite: true,
  lazyLoad: true,
  slidesToScroll: 1,
  slidesToShow: 1,
  speed: 500,
  swipe: false,
  nextArrow: (
    <ArrowContainer>
      <NavigateNextIcon />
    </ArrowContainer>
  ),
  prevArrow: (
    <ArrowContainer>
      <NavigateBeforeIcon />
    </ArrowContainer>
  ),
};

function getStatusDetails(
  status,
  type,
  value,
  labelClassName,
  failedValueClassName,
  successValueClassName
) {
  if (!status) {
    return null;
  }
  return {
    status: status,
    valueType: type,
    value: value,
    labelClassName: labelClassName,
    valueClassName:
      status === constants.BOOKABLE_STATUS.AVAILABLE ? successValueClassName : failedValueClassName,
  };
}
function PropertyList({
  propertyList,
  propertiesWithInvalidOccupations,
  numberOfNights,
  handleGallery,
  setInnerState,
  onMouseEnter,
  onMouseLeave,
  handleSelectProperty,
  onOpenCalendar,
  nature,
  buttonBar,
  showCalendar,
  showCalendarByUrl,
  markerSelected,
  currency,
  withGrid,
  isPropertyChain,
  windowWidth,
}) {
  const onThumbnailClick = ({ event, property }) =>
    handleGallery(
      property.get("media").toJS(),
      property.get("name"),
      property.get("description"),
      null,
      null,
      null,
      event
    );

  return (
    <Container hasButtonBar={buttonBar}>
      <ResultBlock nature={nature} buttonBar={buttonBar} />
      {withGrid ? (
        <EntityGrid
          entities={propertyList.map(property => property.toJS())}
          handleGallery={handleGallery}
          BookableErrorMessage={BookableErrorMessage}
          onSelect={handleSelectProperty}
          handleCalendar={onOpenCalendar}
          numberOfNights={numberOfNights}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          propertiesWithInvalidOccupations={propertiesWithInvalidOccupations.toJS()}
          buildImgUrl={buildImgUrl}
          markerSelected={markerSelected}
        />
      ) : (
        propertyList.map(property => {
          const statusCode =
            property && property.get("lastStatus") && property.get("lastStatus").get("code");
          const propertyTicker = property.get("ticker");
          const rate = property.get("totalRate") && property.get("totalRate") / numberOfNights;
          const statusFilterType =
            property && property.get("lastStatus") && property.get("lastStatus").get("filterType");
          const statusValue =
            property && property.get("lastStatus") && property.get("lastStatus").get("value");
          const media = property.get("media");
          return (
            <StyledPropertyCard
              key={propertyTicker}
              propertyTicker={propertyTicker}
              propertyName={property.get("name")}
              description={property.get("description")}
              available={statusCode === constants.PROPERTY_STATUS.AVAILABLE}
              restricted={statusCode === constants.PROPERTY_STATUS.RESTRICTED}
              unavailable={statusCode === constants.PROPERTY_STATUS.UNAVAILABLE}
              occupancyNotPossible={propertiesWithInvalidOccupations.indexOf(propertyTicker) !== -1}
              markerSelected={markerSelected === propertyTicker}
              calendarOpened={false}
              showCalendar={showCalendar}
              showCalendarByUrl={showCalendarByUrl}
              AmountComponent={<StyledAmount amount={rate} />}
              BookableErrorMessage={
                <BookableErrorMessage
                  hotelCard
                  {...getStatusDetails(statusCode, statusFilterType, statusValue)}
                />
              }
              Slider={
                isPropertyChain && windowWidth < sizes.md ? (
                  <MobileImage
                    src={media && media.get(0) && media.get(0).get("url")}
                    onClick={event => onThumbnailClick({ event, property })}
                  />
                ) : (
                  <Slider {...sliderSettings}>
                    {media.map((image, idx) => (
                      <ThumbnailContainer
                        key={idx}
                        className={classnames("wbkv9-PropertyCard-thumbnailContainer")}
                        onClick={event => onThumbnailClick({ event, property })}>
                        <Thumbnail
                          src={buildImgUrl(
                            image.get("url"),
                            500,
                            750,
                            "https://placehold.it/302x190"
                          )}
                          onHover={value =>
                            setInnerState(val => ({
                              ...val,
                              isThumbnailOnHover: value,
                            }))
                          }
                          title={image && image.get("title")}
                        />
                      </ThumbnailContainer>
                    ))}
                  </Slider>
                )
              }
              ArrowDownIcon={<ArrowDownIcon />}
              onHover={() => onMouseEnter(propertyTicker)}
              onMouseLeave={onMouseLeave}
              handleClick={event => handleSelectProperty({ event, propertyTicker })}
              handleGallery={event => onThumbnailClick({ event, property })}
              handleCalendar={() => onOpenCalendar({ propertyTicker, property })}
              currency={currency}
            />
          );
        })
      )}
    </Container>
  );
}

PropertyList.propTypes = {};
const enhance = withState("innerState", "setInnerState", {
  isThumbnailOnHover: false,
});
export default pure(enhance(PropertyList));
